import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Modal } from "react-bootstrap";
import {
  FaComment,
  FaFilm,
  FaPlay,
  FaStar,
  FaTimes,
  FaUsers,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { moviesAPI } from "../services/api";
import api from "../services/api";
import ModernLoader from "../components/common/ModernLoader";
import { getBackdropUrl, getMoviePosterUrl } from "../utils/imageUtils";
import "../styles/movie-details.css";

const splitMovieValues = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.flatMap((item) => splitMovieValues(item));
  }

  return String(value)
    .split(/[,/|]/)
    .map((part) => part.trim())
    .filter(Boolean);
};

const formatReleaseDate = (dateValue) => {
  if (!dateValue) return "Release date TBA";

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return "Release date TBA";

  return parsedDate.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const getReviewerName = (review) => {
  if (
    review?.userName &&
    review.userName.trim() &&
    review.userName !== "undefined"
  ) {
    return review.userName;
  }

  if (review?.userEmail) {
    return review.userEmail.split("@")[0] || "User";
  }

  return "User";
};

const MovieDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const reviewsRef = useRef(null);

  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({ rating: 0, comment: "" });
  const [showAlert, setShowAlert] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [averageRating, setAverageRating] = useState(0);
  const [totalRatings, setTotalRatings] = useState(0);
  const [failedCastImages, setFailedCastImages] = useState(new Set());

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchMovie();

    // Real-time update listeners
    const handleUpdate = () => {
      console.log("🔄 Silent real-time update on MovieDetails...");
      fetchMovie(true);
    };

    window.addEventListener("movie-updated", handleUpdate);
    window.addEventListener("movies-updated", handleUpdate);
    window.addEventListener("shows-updated", handleUpdate);

    return () => {
      window.removeEventListener("movie-updated", handleUpdate);
      window.removeEventListener("movies-updated", handleUpdate);
      window.removeEventListener("shows-updated", handleUpdate);
    };
  }, [id]);

  const showTimedAlert = (message, type) => {
    setShowAlert({ show: true, message, type });

    window.setTimeout(() => {
      setShowAlert({ show: false, message: "", type: "" });
    }, 3000);
  };

  const fetchMovie = async (isSync = false) => {
    try {
      if (!isSync) setLoading(true);
      setFailedCastImages(new Set());

      const response = await moviesAPI.getById(id);
      const movieData = response?.data;

      if (!movieData) {
        navigate("/movies");
        return;
      }

      setMovie(movieData);
      setReviews(movieData.reviews || []);
      setAverageRating(Number.parseFloat(movieData.averageRating) || 0);
      setTotalRatings(movieData.totalRatings || 0);
    } catch (error) {
      console.error("Error fetching movie:", error);
      navigate("/movies");
    } finally {
      setLoading(false);
    }
  };

  const handleBookTickets = () => {
    if (!movie?._id && !movie?.id) return;
    navigate(`/showtimes/${movie._id || movie.id}`);
  };

  const submitReview = async () => {
    if (!isSignedIn) {
      showTimedAlert("Please sign in to submit a review", "warning");
      return;
    }

    if (newReview.rating === 0 || newReview.comment.trim().length < 5) {
      showTimedAlert(
        "Please add a rating and at least 5 characters in your comment",
        "warning",
      );
      return;
    }

    try {
      await api.post(
        `/ratings/movie/${id}`,
        {
          rating: newReview.rating,
          comment: newReview.comment.trim(),
        },
        {
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
          },
        },
      );

      setNewReview({ rating: 0, comment: "" });
      await fetchMovie();
      showTimedAlert("Review submitted successfully", "success");
    } catch (error) {
      showTimedAlert("Failed to submit review", "danger");
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;

    if (url.includes("embed")) return url;

    if (url.includes("youtube.com/watch?v=")) {
      const videoId = url.split("v=")[1]?.split("&")[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }

    if (url.includes("youtu.be/")) {
      const videoId = url.split("youtu.be/")[1]?.split("?")[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }

    return null;
  };

  const scrollToReviews = () => {
    reviewsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const ratingBreakdown = useMemo(() => {
    const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    if (!Array.isArray(reviews) || reviews.length === 0) return breakdown;

    reviews.forEach((review) => {
      const rating = Number(review?.rating);
      if (rating >= 1 && rating <= 5) {
        breakdown[rating] += 1;
      }
    });

    Object.keys(breakdown).forEach((rating) => {
      breakdown[rating] = Math.round(
        (breakdown[rating] / reviews.length) * 100,
      );
    });

    return breakdown;
  }, [reviews]);

  const movieFormatTags = useMemo(() => {
    if (!movie) return ["2D"];

    const rawTags = [
      ...splitMovieValues(movie.screenType),
      ...splitMovieValues(movie.format),
      ...splitMovieValues(movie.formats),
      ...splitMovieValues(movie.supportedFormats),
    ];

    const uniqueTags = [];
    rawTags.forEach((tag) => {
      const normalized = tag.trim();
      if (!normalized) return;

      const key = normalized.toUpperCase();
      if (uniqueTags.some((existingTag) => existingTag.toUpperCase() === key)) {
        return;
      }

      uniqueTags.push(key);
    });

    if (uniqueTags.length === 0) {
      uniqueTags.push("2D");
    }

    return uniqueTags.slice(0, 4);
  }, [movie]);

  const movieLanguageTags = useMemo(() => {
    if (!movie) return [];

    const uniqueLanguages = [];
    splitMovieValues(movie.language).forEach((language) => {
      const normalized = language.trim();
      if (!normalized) return;

      const key = normalized.toUpperCase();
      if (
        uniqueLanguages.some(
          (existingLanguage) => existingLanguage.toUpperCase() === key,
        )
      ) {
        return;
      }

      uniqueLanguages.push(key);
    });

    return uniqueLanguages.slice(0, 3);
  }, [movie]);

  const genreText = useMemo(() => {
    if (!movie?.genre) return "Genre not available";

    if (Array.isArray(movie.genre)) {
      const cleaned = movie.genre.filter(Boolean);
      return cleaned.length > 0 ? cleaned.join(", ") : "Genre not available";
    }

    return String(movie.genre);
  }, [movie]);

  const castPreviewText = useMemo(() => {
    if (!Array.isArray(movie?.cast) || movie.cast.length === 0) return "";

    return movie.cast
      .slice(0, 6)
      .map((actor) => actor?.name)
      .filter(Boolean)
      .join(", ");
  }, [movie]);

  const sortedReviews = useMemo(() => {
    if (!Array.isArray(reviews)) return [];
    return [...reviews].reverse().slice(0, 8);
  }, [reviews]);

  const trailerEmbedUrl = getYouTubeEmbedUrl(
    movie?.youtubeUrl || movie?.trailer,
  );
  const hasValidTrailer = Boolean(trailerEmbedUrl);

  if (loading) return <ModernLoader text="Loading Movie Details" />;

  if (!movie) {
    return (
      <div className="movie-detail-page">
        <div className="movie-empty-state">
          <h2>Movie not found</h2>
          <button
            type="button"
            className="movie-book-btn"
            onClick={() => navigate("/movies")}
          >
            Back to Movies
          </button>
        </div>
      </div>
    );
  }

  const releaseDate = formatReleaseDate(movie.releaseDate || movie.startDate);
  const duration = movie.duration || movie.runtime || "Runtime TBA";
  const certification = movie.filmGrade || movie.certification || "U/A";
  const heroBackdrop = getBackdropUrl(
    movie.backdrop,
    movie.poster,
    movie.title,
  );

  return (
    <div className="movie-detail-page">
      <section className="movie-hero-section">
        <div
          className="movie-hero-backdrop"
          style={
            heroBackdrop
              ? { backgroundImage: `url(${heroBackdrop})` }
              : undefined
          }
          aria-hidden="true"
        />

        <div className="movie-hero-content">
          <div className="movie-poster-wrapper">
            <img
              src={getMoviePosterUrl(movie.poster, movie.title)}
              alt={movie.title}
              className="movie-poster-image"
              onError={(event) => {
                event.target.src = "https://picsum.photos/280/416";
              }}
            />
          </div>

          <div className="movie-info-wrapper">
            <h1 className="movie-title">{movie.title}</h1>

            <div className="movie-rating-section">
              <div>
                <div
                  className="movie-stars"
                  aria-label={`Average rating ${averageRating.toFixed(1)} out of 5`}
                >
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FaStar
                      key={star}
                      className={`movie-star ${star <= Math.round(averageRating) ? "" : "empty"}`.trim()}
                    />
                  ))}
                </div>
                <p className="movie-rating-count">
                  {totalRatings} {totalRatings === 1 ? "rating" : "ratings"}
                </p>
              </div>

              <p className="movie-rating-text">{averageRating.toFixed(1)}/5</p>

              <button
                type="button"
                className="movie-rate-btn"
                onClick={scrollToReviews}
              >
                Rate now
              </button>
            </div>

            <div className="movie-meta-tags">
              {movieFormatTags.map((tag) => (
                <span key={`format-${tag}`} className="movie-tag">
                  {tag}
                </span>
              ))}

              {movieLanguageTags.map((language) => (
                <span
                  key={`language-${language}`}
                  className="movie-tag movie-tag-accent"
                >
                  {language}
                </span>
              ))}
            </div>

            <div className="movie-meta-info">
              <span>{duration}</span>
              <span className="movie-meta-divider">|</span>
              <span className="movie-genre-text">{genreText}</span>
              <span className="movie-meta-divider">|</span>
              <span className="movie-certification">{certification}</span>
              <span className="movie-meta-divider">|</span>
              <span>{releaseDate}</span>
            </div>

            {movie.director && (
              <div className="movie-director-section">
                <span className="movie-director-label">Director</span>
                <span className="movie-director-name">{movie.director}</span>
              </div>
            )}

            {castPreviewText && (
              <div className="movie-cast-preview">
                <div className="movie-cast-label">Cast</div>
                <div className="movie-cast-names">{castPreviewText}</div>
              </div>
            )}

            <div className="movie-action-buttons">
              <button
                type="button"
                className="movie-book-btn"
                onClick={handleBookTickets}
              >
                Book Tickets
              </button>

              {hasValidTrailer && (
                <button
                  type="button"
                  className="movie-trailer-btn"
                  onClick={() => setShowTrailerModal(true)}
                >
                  <FaPlay size={14} />
                  Watch Trailer
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {movie.description && (
        <section className="movie-about-section">
          <div className="movie-about-card">
            <h2 className="movie-about-title">About the Movie</h2>
            <p className="movie-about-text">{movie.description}</p>
          </div>
        </section>
      )}

      {Array.isArray(movie.cast) && movie.cast.length > 0 && (
        <section className="movie-cast-section">
          <div className="movie-cast-header">
            <FaUsers className="movie-cast-icon" />
            <h2 className="movie-cast-title">Cast</h2>
          </div>

          <div className="movie-cast-grid">
            {movie.cast.map((actor, index) => {
              const actorName = actor?.name || "Actor";
              const castKey = actor?.id || `${actorName}-${index}`;
              const hasProfileImage =
                actor?.profilePath && !failedCastImages.has(castKey);

              return (
                <article key={castKey} className="movie-cast-card">
                  <div className="movie-cast-image-wrapper">
                    {hasProfileImage ? (
                      <img
                        src={actor.profilePath}
                        alt={actorName}
                        className="movie-cast-image"
                        onError={() => {
                          setFailedCastImages((previousSet) => {
                            const nextSet = new Set(previousSet);
                            nextSet.add(castKey);
                            return nextSet;
                          });
                        }}
                      />
                    ) : (
                      <div className="movie-cast-placeholder">
                        {actorName.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  <h3 className="movie-cast-name" title={actorName}>
                    {actorName}
                  </h3>

                  <p
                    className="movie-cast-character"
                    title={actor?.character || ""}
                  >
                    {actor?.character ? `as ${actor.character}` : "Cast"}
                  </p>
                </article>
              );
            })}
          </div>
        </section>
      )}

      <section className="movie-reviews-section" ref={reviewsRef}>
        <div className="movie-reviews-header">
          <FaComment className="movie-reviews-icon" />
          <h2 className="movie-reviews-title">Audience Reviews</h2>
          <p className="movie-reviews-subtitle">
            See what viewers are saying about this movie
          </p>
        </div>

        {showAlert.show && (
          <Alert variant={showAlert.type} className="movie-page-alert">
            {showAlert.message}
          </Alert>
        )}

        <div className="movie-reviews-grid">
          <div>
            {isSignedIn && (
              <div className="movie-review-form">
                <h3 className="movie-review-form-title">Write a Review</h3>

                <div className="movie-review-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`movie-review-star ${star <= newReview.rating ? "active" : ""}`.trim()}
                      onClick={() =>
                        setNewReview((previousReview) => ({
                          ...previousReview,
                          rating: star,
                        }))
                      }
                      aria-label={`Rate ${star} stars`}
                    >
                      <FaStar />
                    </button>
                  ))}
                </div>

                <textarea
                  className="movie-review-textarea"
                  placeholder="Share your thoughts about this movie"
                  value={newReview.comment}
                  onChange={(event) =>
                    setNewReview((previousReview) => ({
                      ...previousReview,
                      comment: event.target.value,
                    }))
                  }
                />

                <button
                  type="button"
                  className="movie-review-submit"
                  onClick={submitReview}
                  disabled={
                    newReview.rating === 0 ||
                    newReview.comment.trim().length < 5
                  }
                >
                  Submit Review
                </button>
              </div>
            )}

            {sortedReviews.length > 0 ? (
              sortedReviews.map((review, index) => {
                const reviewerName = getReviewerName(review);
                const reviewerInitial = reviewerName.charAt(0).toUpperCase();

                return (
                  <article
                    key={review?._id || `${reviewerName}-${index}`}
                    className="movie-review-item"
                  >
                    <div className="movie-review-header">
                      <div className="movie-review-avatar">
                        {reviewerInitial}
                      </div>

                      <div className="movie-review-user-info">
                        <div className="movie-review-user-rating">
                          <div className="movie-review-user-stars">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <FaStar
                                key={star}
                                className={`movie-review-user-star ${star <= Number(review?.rating || 0) ? "" : "empty"}`.trim()}
                              />
                            ))}
                          </div>
                          <span className="movie-review-user-name">
                            {reviewerName}
                          </span>
                        </div>

                        <p className="movie-review-date">
                          {review?.createdAt
                            ? new Date(review.createdAt).toLocaleDateString(
                              "en-GB",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              },
                            )
                            : "Recently"}
                        </p>
                      </div>
                    </div>

                    <p className="movie-review-comment">
                      {review?.comment || "No comment provided"}
                    </p>
                  </article>
                );
              })
            ) : (
              <div className="movie-no-reviews">
                No reviews yet. Be the first to share your thoughts.
              </div>
            )}
          </div>

          <aside className="movie-rating-breakdown">
            <h3 className="movie-rating-breakdown-title">Rating Breakdown</h3>

            {[5, 4, 3, 2, 1].map((rating) => (
              <div key={rating} className="movie-rating-bar-item">
                <span className="movie-rating-bar-label">{rating}★</span>
                <div className="movie-rating-bar-track">
                  <div
                    className="movie-rating-bar-fill"
                    style={{ width: `${ratingBreakdown[rating] || 0}%` }}
                  />
                </div>
                <span className="movie-rating-bar-percent">
                  {ratingBreakdown[rating] || 0}%
                </span>
              </div>
            ))}

            <div className="movie-rating-summary">
              <p className="movie-rating-average">
                {averageRating.toFixed(1)}/5
              </p>
              <p className="movie-rating-total">
                {totalRatings} {totalRatings === 1 ? "review" : "reviews"}
              </p>
            </div>
          </aside>
        </div>
      </section>

      <Modal
        show={showTrailerModal}
        onHide={() => setShowTrailerModal(false)}
        size="xl"
        centered
        dialogClassName="movie-trailer-modal"
      >
        <Modal.Header className="movie-trailer-modal-header">
          <Modal.Title className="movie-trailer-modal-title">
            <FaFilm />
            <span>{movie.title} Trailer</span>
          </Modal.Title>

          <button
            type="button"
            className="movie-trailer-close-btn"
            onClick={() => setShowTrailerModal(false)}
            aria-label="Close trailer"
          >
            <FaTimes />
          </button>
        </Modal.Header>

        <Modal.Body className="movie-trailer-modal-body">
          {trailerEmbedUrl ? (
            <div className="movie-trailer-frame-wrap">
              <iframe
                src={trailerEmbedUrl}
                title={`${movie.title} Trailer`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="movie-no-reviews">
              Trailer is currently unavailable.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MovieDetails;
